'use client';

import * as Avatar from '@radix-ui/react-avatar';
import * as Dialog from '@radix-ui/react-dialog';
import { Badge, Box, Flex, Table, Text } from '@radix-ui/themes';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  User,
  X,
  Edit,
  Info
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface AuditLog {
  id: string;
  timestamp: string | Date;
  userId: string;
  userName: string;
  userEmail: string;
  action: 'CREATE' | 'EDIT' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'EXPORT' | 'IMPORT' | 'VIEW' | 'DOWNLOAD' | 'UPLOAD';
  resourceType: 'BLOG' | 'FAQ' | 'USER' | 'MEDIA' | 'SERVICE' | 'SEO' | 'SYSTEM' | 'PAGE' | 'FILE';
  resourceId: string;
  resourceName: string;
  details: string;
  ipAddress: string;
  userAgent: string;
  status: 'SUCCESS' | 'FAILURE' | 'PENDING';
  metadata?: Record<string, unknown>;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: PaginationInfo;
}

export interface AuditLogsFilters {
  search: string;
  action: string;
  resourceType: string;
  status: string;
  startDate: string;
  endDate: string;
}

interface AuditLogsListProps {
  filters: AuditLogsFilters;
  onRefresh?: () => void;
}

export default function AuditLogsList({ filters }: AuditLogsListProps) {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  const memoFilters = useMemo(() => filters, [filters]);

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Validate date range before fetching
      if (memoFilters.startDate && memoFilters.endDate && new Date(memoFilters.startDate) > new Date(memoFilters.endDate)) {
        throw new Error('Start date must be before end date');
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: '5',
        search: memoFilters.search || '',
        action: memoFilters.action || '',
        resourceType: memoFilters.resourceType || '',
        status: memoFilters.status || '',
        startDate: memoFilters.startDate || '',
        endDate: memoFilters.endDate || ''
      });

      const response = await fetch(`/api/admin/audit-logs?${params}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch audit logs' }));
        throw new Error(errorData.error || `Failed to fetch audit logs (${response.status})`);
      }

      const data: AuditLogsResponse = await response.json();

      if (!data || !Array.isArray(data.logs)) {
        throw new Error('Invalid response format from server');
      }

      setAuditLogs(data.logs);
      setPagination(data.pagination);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      setAuditLogs([]);
      console.error('Fetch audit logs error:', err);
    } finally {
      setLoading(false);
    }
  }, [memoFilters, page]);

  useEffect(() => {
     
    setPage(1); // Reset to first page when filters change
  }, [memoFilters]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE': return <Plus className="w-4 h-4 text-green-600" />;
      case 'EDIT': return <Edit className="w-4 h-4 text-blue-600" />;
      case 'DELETE': return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'LOGIN': return <User className="w-4 h-4 text-purple-600" />;
      default: return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'FAILURE': return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'PENDING': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatTimestamp = (timestamp: string | Date) => {
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid Date';
    }
  };

  if (loading && auditLogs.length === 0) {
    return (
      <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
            <Text size="3" weight="medium" className="text-gray-900 mb-2">
              Loading Audit Logs...
            </Text>
          </div>
        </div>
      </Box>
    );
  }

  if (error && auditLogs.length === 0) {
    return (
      <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <Text size="3" weight="medium" className="text-gray-900 mb-2">
            Error Loading Audit Logs
          </Text>
          <Text size="2" className="text-red-600 mb-4">{error}</Text>
          <button
            onClick={() => fetchAuditLogs()}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2 mx-auto"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Try Again</span>
          </button>
        </div>
      </Box>
    );
  }

  return (
    <>
      {/* Results Summary */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Text size="2" className="text-gray-600">
            Showing <span className="font-medium">{auditLogs.length}</span> of <span className="font-medium">{pagination.total}</span> logs
          </Text>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2">
              <Text size="2" className="text-red-600">Error: {error}</Text>
              <button
                onClick={() => fetchAuditLogs()}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Retry
              </button>
            </div>
          )}
        </div>
        <Text size="2" className="text-gray-500">
          {pagination.totalPages > 0 ? `Page ${pagination.page} of ${pagination.totalPages}` : 'No pages'}
        </Text>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <Table.Root>
          <Table.Header>
            <Table.Row className="bg-gray-50">
              <Table.ColumnHeaderCell>Timestamp</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>User</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Action</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Resource</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Details</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Loading audit logs...
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : error ? (
              <Table.Row>
                <Table.Cell colSpan={7} className="px-6 py-8 text-center text-red-500">
                  {error}
                </Table.Cell>
              </Table.Row>
            ) : auditLogs.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No audit logs found
                </Table.Cell>
              </Table.Row>
            ) : (
              auditLogs.map((log, index) => (
                <Table.Row key={log.id || `log-${index}`} className="hover:bg-gray-50">
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <Text size="2">{formatTimestamp(log.timestamp)}</Text>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Avatar.Root className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Avatar.Fallback className="w-4 h-4 text-blue-600">
                          <User className="w-4 h-4 text-blue-600" />
                        </Avatar.Fallback>
                      </Avatar.Root>
                      <div>
                        <Text size="2" weight="medium">{log.userName || 'Unknown'}</Text>
                        <Text size="1" className="text-gray-500">{log.userEmail || 'N/A'}</Text>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getActionIcon(log.action)}
                      <Text size="2" weight="medium">{log.action || 'N/A'}</Text>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div>
                      <Text size="2" weight="medium">{log.resourceType || 'N/A'}</Text>
                      <Text size="1" className="text-gray-500">{log.resourceName || 'N/A'}</Text>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" className="max-w-xs truncate" title={log.details || ''}>
                      {log.details || 'No details'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <Badge
                        variant="soft"
                        color={
                          log.status === 'SUCCESS' ? 'green' :
                          log.status === 'FAILURE' ? 'red' : 'yellow'
                        }
                      >
                        {log.status || 'UNKNOWN'}
                      </Badge>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                      aria-label="View log details"
                    >
                      <Eye className="w-4 h-4" />
                      <Text size="2">View</Text>
                    </button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <Box className="px-6 py-4 bg-gray-50 rounded-md">
          <Flex justify="between" align="center">
            <Text size="2" className="text-gray-700">
              Showing <Text weight="medium">{(pagination.page - 1) * 5 + 1}</Text> to{' '}
              <Text weight="medium">{Math.min(pagination.page * 5, pagination.total)}</Text> of{' '}
              <Text weight="medium">{pagination.total}</Text> results
            </Text>
            <Flex gap="2">
              <button
                className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 px-2 py-1.5 text-sm font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={pagination.page === 1 || loading}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm font-semibold shadow-sm transition-all duration-200 min-w-[36px] ${pagination.page === pageNum
                      ? 'border-blue-300 bg-blue-100 text-blue-800 shadow'
                      : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50 hover:shadow'
                      }`}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 px-2 py-1.5 text-sm font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={pagination.page === pagination.totalPages || loading}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Log Details Modal */}
      <Dialog.Root open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
          <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-medium text-gray-900">Audit Log Details</Dialog.Title>
                  <Dialog.Close asChild>
                    <button className="text-gray-400 hover:text-gray-600">
                      <X className="w-6 h-6" />
                    </button>
                  </Dialog.Close>
                </div>

                {selectedLog && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Timestamp:</span>
                        <div className="font-medium">{formatTimestamp(selectedLog.timestamp)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Action:</span>
                        <div className="font-medium">{selectedLog.action || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">User:</span>
                        <div className="font-medium">{selectedLog.userName || 'Unknown'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Email:</span>
                        <div className="font-medium">{selectedLog.userEmail || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <div className="font-medium">{selectedLog.status || 'UNKNOWN'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Resource Type:</span>
                        <div className="font-medium">{selectedLog.resourceType || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Resource Name:</span>
                        <div className="font-medium">{selectedLog.resourceName || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">IP Address:</span>
                        <div className="font-medium">{selectedLog.ipAddress || 'N/A'}</div>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500 text-sm">Details:</span>
                      <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                        {selectedLog.details || 'No details available'}
                      </div>
                    </div>

                    {selectedLog.userAgent && (
                      <div>
                        <span className="text-gray-500 text-sm">User Agent:</span>
                        <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-3 rounded-md break-all">
                          {selectedLog.userAgent}
                        </div>
                      </div>
                    )}

                    {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                      <div>
                        <span className="text-gray-500 text-sm">Metadata:</span>
                        <div className="mt-1 bg-gray-50 p-3 rounded-md overflow-x-auto">
                          <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                            {JSON.stringify(selectedLog.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end pt-4">
                      <Dialog.Close asChild>
                        <button className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
                          Close
                        </button>
                      </Dialog.Close>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

